import { useEffect, useCallback, memo } from 'react';
import { ChangelogWidgetProps } from './config';
import { CANNY_SDK_ID, CANNY_SDK_URL } from '../../config';
import { AnimatedBell, ChangelogButton } from './styled';
import { Text } from '@shopify/polaris';
import { BellIcon } from '@/icons';

declare global {
  interface Window {
    Canny: any;
  }
}

const ChangelogWidget: React.FC<ChangelogWidgetProps> = ({
  appID = process.env.REACT_APP_CANNY_APP_ID,
  position = 'bottom',
  align = 'left',
  theme = 'light',
  onLoad,
}) => {
  const initializeChangelog = useCallback(() => {
    if (!window.Canny) return;

    try {
      window.Canny('initChangelog', {
        appID,
        position,
        align,
        theme,
      });

      onLoad?.();
    } catch (error) {
      console.error('Failed to initialize Changelog widget:', error);
    }
  }, [appID, position, align, theme, onLoad]);

  const loadCannySDK = useCallback(() => {
    // Check if SDK is already loaded
    if (document.getElementById(CANNY_SDK_ID)) {
      // If SDK is loaded, initialize changelog
      if (window.Canny) {
        initializeChangelog();
      }
      return;
    }

    const script = document.createElement('script');
    script.id = CANNY_SDK_ID;
    script.src = CANNY_SDK_URL;
    script.async = true;
    script.onload = () => {
      initializeChangelog();
    };
    script.onerror = () => {
      console.error('Failed to load Canny SDK');
    };

    document.head.appendChild(script);
  }, [initializeChangelog]);

  // Load SDK and initialize changelog when component mounts
  useEffect(() => {
    loadCannySDK();
  }, [loadCannySDK]);

  // Re-initialize when SDK becomes available
  useEffect(() => {
    if (window.Canny) {
      initializeChangelog();
    }
  }, [initializeChangelog]);

  return (
    <ChangelogButton data-canny-changelog>
      <AnimatedBell>
        <BellIcon width={18} height={18} />
      </AnimatedBell>
      <Text as="span" variant="bodyMd">
        What's New?
      </Text>
    </ChangelogButton>
  );
};

export default memo(ChangelogWidget);
